import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SCHEDULED_ASSIGNMENTS_EVENTS } from 'src/constants/local-storage.constants';
import { NormalizedRealtimePayload } from 'src/interfaces/api.types';
import { ScheduledAssignmentEventsState } from 'src/interfaces/store.types';
import type { RootState } from 'src/store';
import { useAppSelector } from 'src/store/redux';
import { dayjs } from 'src/utils/dayjs-config.utils';

const getInitialState = (): NormalizedRealtimePayload[] => {
  const storedState = localStorage.getItem(SCHEDULED_ASSIGNMENTS_EVENTS);

  return storedState ? JSON.parse(storedState) : [];
};

const initialState: ScheduledAssignmentEventsState = getInitialState();

export const scheduledAssignmentEventsSlice = createSlice({
  name: 'scheduledAssignmentEvents',
  initialState,
  reducers: {
    addScheduledAssignmentsEvent: (
      state,
      action: PayloadAction<NormalizedRealtimePayload>,
    ) => {
      state.push(action.payload);
    },
    removeScheduledAssignmentsEvent: (
      state,
      action: PayloadAction<NormalizedRealtimePayload>,
    ) => {
      const actionItem = action.payload;

      return state.filter(
        (item) => item.activitiesBks[0] !== actionItem.activitiesBks[0],
      );
    },
    updateScheduledAssignmentsEvent: (
      state,
      action: PayloadAction<NormalizedRealtimePayload>,
    ) => {
      const actionItem = action.payload;

      const existingEventIndex = state.findIndex(
        (item: NormalizedRealtimePayload) =>
          item.activitiesBks[0] === actionItem.activitiesBks[0],
      );

      if (existingEventIndex !== -1) {
        // remove if startDate is expired
        if (dayjs(actionItem.startDate).diff(dayjs()) < 0) {
          return state.filter(
            (item) => item.activitiesBks[0] !== actionItem.activitiesBks[0],
          );
        }

        const existingEvent = state[existingEventIndex];

        // update existing if startDate updated
        if (existingEvent.startDate !== actionItem.startDate) {
          state[existingEventIndex] = actionItem;
        }
      }
      // add to the queue if startDate is available
      else if (dayjs(actionItem.startDate).diff(dayjs()) >= 0) {
        state.push(actionItem);
      }
    },
  },
});

export const {
  addScheduledAssignmentsEvent,
  removeScheduledAssignmentsEvent,
  updateScheduledAssignmentsEvent,
} = scheduledAssignmentEventsSlice.actions;

export const useScheduledAssignmentsEventsSelector = () =>
  useAppSelector((state: RootState) => state.scheduledAssignmentEvents);

export default scheduledAssignmentEventsSlice.reducer;
